import { useState } from "react";
import { FaTimes, FaPlus } from "react-icons/fa";
import api, { BACKEND_URL } from '../api/axios';

const COLORS = { blue: "#2563EB", orange: "#F59E0B", dark: "#0D1B3D" };

export default function CandidateNotesModal({ application, onClose, onSaved }) {
  const [notes, setNotes] = useState(application.notes || "");
  const [tags, setTags] = useState(application.tags || []);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  };

  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put(`/pipeline/${application.id}/notes`, { notes });
      await api.put(`/pipeline/${application.id}/tags`, { tags });
      onSaved();
      onClose();
    } catch {
      setError("Couldn't save notes and tags.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div
          className="flex items-center justify-between rounded-t-xl px-6 py-4"
          style={{ backgroundColor: COLORS.dark }}
        >
          <h2 className="text-lg font-semibold text-white">{application.candidate_name}</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <FaTimes />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium" style={{ color: COLORS.dark }}>
              Tags
            </label>
            <div className="mb-2 flex flex-wrap gap-2">
              {tags.map((t) => (
                <span
                  key={t}
                  className="flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium"
                  style={{ backgroundColor: "#EFF6FF", color: COLORS.blue }}
                >
                  {t}
                  <button onClick={() => removeTag(t)} className="text-blue-400 hover:text-blue-700">
                    <FaTimes size={10} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                placeholder="Add a tag"
                className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
              />
              <button
                onClick={addTag}
                className="rounded-md border border-gray-300 px-3 text-gray-600"
              >
                <FaPlus />
              </button>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium" style={{ color: COLORS.dark }}>
              Internal Notes
            </label>
            <textarea
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2"
              placeholder="Notes visible to your team only..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
            style={{ backgroundColor: COLORS.blue }}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}